import React, { useState, useRef, useCallback, useEffect } from "react";
import SimpleChat from '../components/SimpleChat';
import { useParams, useLocation } from "wouter";
import { useToast } from '../hooks/use-toast';
import StatusBadge from '../components/shared/status-badge';
import { 
  Tabs, 
  TabsContent, 
  TabsList, 
  TabsTrigger 
} from '../components/ui/tabs';
import { 
  Card, 
  CardContent, 
  CardDescription, 
  CardHeader, 
  CardTitle,
  CardFooter 
} from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { format } from "date-fns";
import { formatCurrency } from '../lib/utils';
import { formatPhone } from '../lib/validation';
import { 
  FileText, 
  Receipt, 
  Calendar, 
  MessageCircle,
  Clock,
  DollarSign,
  User,
  Mail,
  Phone,
  MapPin,
  CheckCircle,
  AlertCircle,
  Download,
  Eye,
  Send,
  Building,
  Activity,
  X,
  Home,
  FolderOpen
} from "lucide-react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "../hooks/use-toast";
import { generatePdfHtml, PdfTemplateConfig, DEFAULT_PDF_TEMPLATE_CONFIG } from '../components/pdf/unified-pdf-template';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '../components/ui/alert-dialog';


// Error Boundary Component
function ErrorBoundary({ children }: { children: React.ReactNode }) {
  const [error, setError] = useState<Error | null>(null);
  if (error) {
    return <div className="text-red-500 p-8">
      <h2>Something went wrong in the Client Portal:</h2>
      <pre>{error.message}</pre>
      <pre>{error.stack}</pre>
    </div>;
  }
  return <ErrorCatcher onError={setError}>{children}</ErrorCatcher>;
}

class ErrorCatcher extends React.Component<{ onError: (e: Error) => void; children: React.ReactNode }, {}> {
  componentDidCatch(error: Error) {
    this.props.onError(error);
  }
  render() {
    return this.props.children;
  }
}

// Client Portal Component
export default function ClientPortal() {
  const { clientId } = useParams<{ clientId: string }>();
  const [, setLocation] = useLocation();
  const [activeTab, setActiveTab] = useState("overview");
  const [unreadMessages, setUnreadMessages] = useState(0);
  const { toast } = useToast();
  
  // Tab change handler with visual feedback
  const handleTabChange = (newTab: string) => {
    setActiveTab(newTab);
    
    // Only show toast on desktop (not mobile)
    const isDesktop = window.innerWidth >= 640; // sm breakpoint
    
    if (isDesktop) {
      // Tab labels with icons
      const tabLabels: Record<string, { label: string; icon: string }> = {
        overview: { label: "Overview", icon: "🏠" },
        projects: { label: "Projects", icon: "🏗️" },
        estimates: { label: "Estimates", icon: "📋" },
        invoices: { label: "Invoices", icon: "💰" },
        schedule: { label: "Schedule", icon: "📅" },
        messages: { label: "Messages", icon: "💬" }
      };
      
      const currentTab = tabLabels[newTab];
      
      toast({
        title: `${currentTab.icon} ${currentTab.label}`,
        description: `Now viewing ${currentTab.label.toLowerCase()}`,
        duration: 2000,
        className: "bg-white border-green-500 border-2",
      });
    }
  };
  
  // Real client information from database
  const [clientInfo, setClientInfo] = useState<any>(null);
  const [clientProjects, setClientProjects] = useState<any[]>([]);
  const [clientEstimates, setClientEstimates] = useState<any[]>([]);
  const [clientInvoices, setClientInvoices] = useState<any[]>([]);
  const [clientAppointments, setClientAppointments] = useState<any[]>([]);
  const [agentInfo, setAgentInfo] = useState<any>(null);
  const [loading, setLoading] = useState(true);

  const fetchClientData = async () => {
    try {
      // Get token from URL parameters
      const urlParams = new URLSearchParams(window.location.search);
      const token = urlParams.get('token');
      
      if (!token) {
        console.error('[Client Portal] No access token provided');
        setClientInfo(null);
        return;
      }
      
      const response = await fetch(`/api/client-portal-data?clientId=${clientId}&token=${token}`);
      if (response.ok) {
        const data = await response.json();
        console.log('[Client Portal] Fetched data:', data);
        setClientInfo(data.client);
        setClientProjects(data.projects || []);
        setClientEstimates(data.estimates || []);
        setClientInvoices(data.invoices || []);
        setClientAppointments(data.appointments || []);
        setAgentInfo(data.agent);
      } else {
        const errorData = await response.json();
        console.error('[Client Portal] API error:', response.status, errorData);
        setClientInfo(null);
      }
    } catch (error) {
      console.error('Error fetching client data:', error);
      setClientInfo(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (clientId) {
      fetchClientData();
    }
  }, [clientId]);

  const refetchEstimates = () => {
    fetchClientData();
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-white dark:bg-slate-900 flex items-center justify-center px-4">
        <div className="text-center">
          <div className="animate-spin rounded-full h-10 w-10 sm:h-12 sm:w-12 border-b-2 border-green-500 mx-auto"></div>
          <p className="mt-4 text-slate-700 text-base sm:text-lg text-center">Loading client portal...</p>
        </div>
      </div>
    );
  }

  // Defensive: If any required data is missing, show a clear error message
  if (!loading && (!clientInfo || typeof clientInfo !== 'object')) {
    console.error('[Client Portal] clientInfo missing or invalid:', clientInfo);
    return <div className="remodra-layout">
      <div className="remodra-main-mobile">
        <div className="remodra-content">
          <div className="flex items-center justify-center min-h-screen">
            <div className="text-center">
              <div className="remodra-empty-icon mb-4">⚠️</div>
              <p className="remodra-empty-title text-red-400">Client not found or failed to load client data.</p>
              <p className="remodra-empty-description">Please check the client link or try again later.</p>
            </div>
          </div>
        </div>
      </div>
    </div>;
  }
  if (!loading && (!Array.isArray(clientProjects) || !Array.isArray(clientEstimates) || !Array.isArray(clientInvoices) || !Array.isArray(clientAppointments))) {
    console.error('[Client Portal] One or more data arrays are missing or invalid:', { clientProjects, clientEstimates, clientInvoices, clientAppointments });
    return <div className="remodra-layout">
      <div className="remodra-main-mobile">
        <div className="remodra-content">
          <div className="flex items-center justify-center min-h-screen">
            <div className="text-center">
              <div className="remodra-empty-icon mb-4">⚠️</div>
              <p className="remodra-empty-title text-red-400">Client data is incomplete or corrupted.</p>
              <p className="remodra-empty-description">Please contact support.</p>
            </div>
          </div>
        </div>
      </div>
    </div>;
  }

  // Use real data from database
  const projects = clientProjects;
  const estimates = clientEstimates;
  const invoices = clientInvoices;
  const appointments = clientAppointments;

  const handleViewEstimate = async (estimateId: string) => {
    try {
      // Get portal token from URL (always available in client portal)
      const urlParams = new URLSearchParams(window.location.search);
      const portalToken = urlParams.get('token');
      
      // Find the estimate in our list to get its token (if available)
      const estimate = clientEstimates.find((e: any) => e.id === parseInt(estimateId));
      
      // Prefer estimate-specific token, but fallback to portal token
      const tokenToUse = (estimate && estimate.estimateToken) ? estimate.estimateToken : portalToken;
      
      if (tokenToUse) {
        const publicEstimateUrl = `/public/estimates/${estimateId}?token=${tokenToUse}`;
        window.open(publicEstimateUrl, '_blank');
      } else {
        toast({
          title: "Error",
          description: "Could not generate secure link. Please contact support.",
          variant: "destructive",
        });
      }
    } catch (error) {
      console.error('Error opening estimate:', error);
      toast({
        title: "Error",
        description: "Could not open estimate. Please try again.",
        variant: "destructive",
      });
    }
  };

  const handleViewInvoice = async (invoiceId: string) => {
    try {
      // Find the invoice in our list
      const invoice = clientInvoices.find((i: any) => i.id === parseInt(invoiceId));
      
      if (!invoice) {
        toast({
          title: "Error",
          description: "Invoice not found.",
          variant: "destructive",
        });
        return;
      }

      // Fetch contractor data to get company name and PDF config (single API call)
      let contractorCompanyName = 'REMODRA';
      let pdfConfig: PdfTemplateConfig | null = null;
      if (invoice.contractorId) {
        try {
          const contractorResponse = await fetch(`/api/public/contractor/${invoice.contractorId}/settings`);
          if (contractorResponse.ok) {
            const contractorData = await contractorResponse.json();
            if (contractorData.companyName) {
              contractorCompanyName = contractorData.companyName;
            }
            if (contractorData.pdfTemplate) {
              pdfConfig = contractorData.pdfTemplate;
            }
          }
        } catch (err) {
          console.error('Error loading contractor data:', err);
        }
      }

      // Use invoice data we already have from client-portal-data (now includes items)
      const invoiceData = {
        ...invoice,
        items: invoice.items || [],
        client: clientInfo || {},
        contractor: {
          companyName: contractorCompanyName,
          id: invoice.contractorId
        }
      };

      const config = pdfConfig ? { ...DEFAULT_PDF_TEMPLATE_CONFIG, ...pdfConfig } : DEFAULT_PDF_TEMPLATE_CONFIG;

      // Open print window
      const printWindow = window.open('', '_blank');
      if (!printWindow) {
        toast({
          title: "Popup Blocked",
          description: "Please allow pop-ups to view invoices.",
          variant: "destructive"
        });
        return;
      }

      // Generate PDF HTML
      const htmlContent = generatePdfHtml({
        data: {
          ...invoiceData,
          items: invoiceData.items || [],
          client: invoiceData.client || {},
          contractor: invoiceData.contractor || {
            companyName: 'REMODRA'
          }
        },
        config,
        type: 'invoice',
        isSigned: !!invoiceData.clientSignature
      });

      printWindow.document.write(htmlContent);
      printWindow.document.close();
    } catch (error: any) {
      console.error('Error viewing invoice:', error);
      toast({
        title: "Error",
        description: error.message || "Failed to view invoice. Please try again.",
        variant: "destructive",
      });
    }
  };

  const handleDownloadInvoice = async (invoice: any) => {
    try {
      // Fetch contractor data to get company name, logo, and PDF config (single API call)
      let contractorCompanyName = 'REMODRA';
      let contractorLogoUrl: string | null = null;
      let pdfConfig: PdfTemplateConfig | null = null;
      if (invoice.contractorId) {
        try {
          const contractorResponse = await fetch(`/api/public/contractor/${invoice.contractorId}/settings`);
          if (contractorResponse.ok) {
            const contractorData = await contractorResponse.json();
            if (contractorData.companyName) {
              contractorCompanyName = contractorData.companyName;
            }
            if (contractorData.logoUrl) {
              contractorLogoUrl = contractorData.logoUrl;
            }
            if (contractorData.pdfTemplate) {
              pdfConfig = contractorData.pdfTemplate;
            }
          }
        } catch (err) {
          console.error('Error loading contractor data:', err);
        }
      }

      // Use invoice data we already have from client-portal-data (now includes items)
      const invoiceData = {
        ...invoice,
        items: invoice.items || [],
        client: clientInfo || {},
        contractor: {
          companyName: contractorCompanyName,
          logoUrl: contractorLogoUrl, // Include logo URL
          id: invoice.contractorId
        }
      };

      const config = pdfConfig ? { ...DEFAULT_PDF_TEMPLATE_CONFIG, ...pdfConfig } : DEFAULT_PDF_TEMPLATE_CONFIG;

      // Open print window
      const printWindow = window.open('', '_blank');
      if (!printWindow) {
        toast({
          title: "Popup Blocked",
          description: "Please allow pop-ups to download invoices.",
          variant: "destructive"
        });
        return;
      }

      // Generate PDF HTML
      const htmlContent = generatePdfHtml({
        data: {
          ...invoiceData,
          items: invoiceData.items || [],
          client: invoiceData.client || {},
          contractor: invoiceData.contractor || {
            companyName: 'REMODRA',
            logoUrl: contractorLogoUrl // Ensure logo is passed
          }
        },
        config,
        type: 'invoice',
        isSigned: !!invoiceData.clientSignature
      });

      printWindow.document.write(htmlContent);
      printWindow.document.close();
      
      // Wait a bit for content to load, then trigger print
      setTimeout(() => {
        printWindow.print();
      }, 250);
      
      toast({
        title: "PDF Download",
        description: "Print dialog opened. Select 'Save as PDF' to download.",
      });
    } catch (error: any) {
      console.error('Error downloading invoice:', error);
      toast({
        title: "Error",
        description: error.message || "Failed to download invoice. Please try again.",
        variant: "destructive",
      });
    }
  };

  const handleApproveEstimate = async (estimateId: string) => {
    try {
      // Get portal token from URL (always available in client portal)
      const urlParams = new URLSearchParams(window.location.search);
      const portalToken = urlParams.get('token');
      
      // Find the estimate in our list to get its token (if available)
      const estimate = clientEstimates.find((e: any) => e.id === parseInt(estimateId));
      
      // Prefer estimate-specific token, but fallback to portal token
      const tokenToUse = (estimate && estimate.estimateToken) ? estimate.estimateToken : portalToken;
      
      if (tokenToUse) {
        // Add action=approve parameter to auto-open signature dialog
        const publicEstimateUrl = `/public/estimates/${estimateId}?token=${tokenToUse}&action=approve`;
        toast({
          title: "Opening Estimate",
          description: "Please sign and approve the estimate in the new window.",
        });
        window.open(publicEstimateUrl, '_blank');
      } else {
        toast({
          title: "Error",
          description: "Could not open estimate. Please contact support.",
          variant: "destructive",
        });
      }
    } catch (error) {
      console.error('Error opening estimate:', error);
      toast({
        title: "Error",
        description: "Failed to open estimate. Please try again.",
        variant: "destructive",
      });
    }
  };

  const handlePayInvoice = (invoiceId: string) => {
    alert(`Redirecting to payment portal for invoice ${invoiceId}`);
  };

  const handleRescheduleAppointment = (appointmentId: number) => {
    alert(`Rescheduling appointment ${appointmentId}. A calendar will open.`);
  };

  const handleCancelAppointment = async (appointmentId: number) => {
    try {
      // Get portal token from URL
      const urlParams = new URLSearchParams(window.location.search);
      const portalToken = urlParams.get('token');
      
      if (!portalToken) {
        toast({
          title: "Error",
          description: "Authentication required. Please use the portal link provided.",
          variant: "destructive",
        });
        return;
      }

      const response = await fetch(`/api/client-action`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          appointmentId: appointmentId,
          action: 'cancel',
          clientId: clientId,
          token: portalToken,
          notes: 'Cancelled by client through portal'
        }),
      });

      if (response.ok) {
        const result = await response.json();
        toast({
          title: "Appointment Cancelled",
          description: "Your appointment has been cancelled successfully.",
        });
        // Refresh appointments list
        fetchClientData();
      } else {
        const errorData = await response.json();
        toast({
          title: "Error",
          description: errorData.message || "Failed to cancel appointment. Please try again.",
          variant: "destructive",
        });
      }
    } catch (error) {
      console.error('Error cancelling appointment:', error);
      toast({
        title: "Error",
        description: "Failed to cancel appointment. Please try again.",
        variant: "destructive",
      });
    }
  };

  const getStatusBadge = (status: string, type: 'project' | 'estimate' | 'invoice' = 'project') => {
    // For client portal, we need to determine the type based on context
    // This is a simplified approach - in a real app you might pass the type explicitly
    if (status === 'approved' || status === 'paid' || status === 'sent') {
      return <StatusBadge status={status} type="estimate" />;
    }
    return <StatusBadge status={status} type={type} />;
  };

  // Overview Tab Component
  const OverviewTab = () => (
    <div className="space-y-4 sm:space-y-6 md:space-y-8">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-2 sm:gap-4">
        <h2 className="text-xl sm:text-2xl font-bold text-green-500 flex items-center gap-2">
          <Activity className="h-5 w-5 sm:h-6 sm:w-6" />
          Overview
        </h2>
        <div className="text-xs sm:text-sm text-slate-600">Summary of your account activity</div>
      </div>
      
      {/* Stats Grid - Mobile Optimized */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 md:gap-6">
        <div className="remodra-stats-card">
          <div className="flex items-center justify-between">
            <div>
              <div className="remodra-stats-label">Active Projects</div>
              <div className="remodra-stats-number">{projects.filter(p => p.status === 'in_progress' || p.status === 'In Progress').length}</div>
            </div>
            <Building className="h-6 w-6 sm:h-8 sm:w-8 text-green-500 flex-shrink-0" />
          </div>
          <div className="remodra-stats-accent"></div>
        </div>
        
        <div className="remodra-stats-card">
          <div className="flex items-center justify-between">
            <div>
              <div className="remodra-stats-label">Pending Estimates</div>
              <div className="remodra-stats-number">{estimates.filter(e => e.status === 'draft' || e.status === 'pending').length}</div>
            </div>
            <FileText className="h-6 w-6 sm:h-8 sm:w-8 text-green-500 flex-shrink-0" />
          </div>
          <div className="remodra-stats-accent"></div>
        </div>
        
        <div className="remodra-stats-card">
          <div className="flex items-center justify-between">
            <div>
              <div className="remodra-stats-label">Outstanding Invoices</div>
              <div className="remodra-stats-number">{invoices.filter(i => i.status === 'pending' || i.status === 'sent').length}</div>
            </div>
            <Receipt className="h-6 w-6 sm:h-8 sm:w-8 text-green-500 flex-shrink-0" />
          </div>
          <div className="remodra-stats-accent"></div>
        </div>
        
        <div className="remodra-stats-card">
          <div className="flex items-center justify-between">
            <div>
              <div className="remodra-stats-label">Upcoming Appointments</div>
              <div className="remodra-stats-number">{appointments.filter(a => new Date(a.startTime) > new Date()).length}</div>
            </div>
            <Calendar className="h-6 w-6 sm:h-8 sm:w-8 text-green-500 flex-shrink-0" />
          </div>
          <div className="remodra-stats-accent"></div>
        </div>
      </div>

      {/* Agent Contact Information - Mobile Optimized */}
      {agentInfo && (
        <div className="remodra-card p-4 sm:p-6">
          <div className="flex items-center gap-3 mb-4 sm:mb-6">
            <div className="w-10 h-10 sm:w-12 sm:h-12 bg-gradient-to-br from-green-500 to-green-700 rounded-lg flex items-center justify-center flex-shrink-0">
              <User className="h-5 w-5 sm:h-6 sm:w-6 text-white" />
            </div>
            <div>
              <h3 className="text-lg sm:text-xl font-bold text-green-500">Your Assigned Agent</h3>
              <p className="text-xs sm:text-sm text-slate-600">Contact your project coordinator</p>
            </div>
          </div>
          <div className="grid grid-cols-1 gap-3 sm:gap-4">
            <div className="flex items-center gap-3 p-3 sm:p-4 bg-gradient-to-r from-slate-50 to-slate-100 rounded-lg border border-slate-200">
              <div className="w-10 h-10 bg-green-500 rounded-full flex items-center justify-center flex-shrink-0">
                <User className="h-5 w-5 text-white" />
              </div>
              <div className="min-w-0 flex-1">
                <p className="font-medium text-slate-800 truncate">{agentInfo.name}</p>
              </div>
            </div>
            <div className="flex items-center gap-3 p-3 sm:p-4 bg-gradient-to-r from-slate-50 to-slate-100 rounded-lg border border-slate-200">
              <div className="w-10 h-10 bg-green-500 rounded-full flex items-center justify-center flex-shrink-0">
                <Mail className="h-5 w-5 text-white" />
              </div>
              <div className="min-w-0 flex-1">
                <p className="font-medium text-slate-800 truncate">{agentInfo.email}</p>
              </div>
            </div>
            <div className="flex items-center gap-3 p-3 sm:p-4 bg-gradient-to-r from-slate-50 to-slate-100 rounded-lg border border-slate-200">
              <div className="w-10 h-10 bg-green-500 rounded-full flex items-center justify-center flex-shrink-0">
                <Phone className="h-5 w-5 text-white" />
              </div>
              <div className="min-w-0 flex-1">
                <p className="font-medium text-slate-800 whitespace-nowrap">{formatPhone(agentInfo.phone)}</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Recent Activity */}
      <Card className="bg-gradient-to-br from-slate-50 to-gray-50 border-slate-200 shadow-lg">
        <CardHeader className="bg-gradient-to-r from-slate-100 to-gray-100 rounded-t-lg">
          <CardTitle className="text-slate-800 flex items-center gap-2">
            <Activity className="h-5 w-5 text-slate-600" />
            Recent Activity
          </CardTitle>
          <CardDescription className="text-slate-600">Your latest project updates and communications</CardDescription>
        </CardHeader>
        <CardContent className="p-6">
          <div className="space-y-4">
            {projects.length === 0 && estimates.length === 0 && invoices.length === 0 ? (
              <div className="text-center py-8">
                <Activity className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                <p className="text-gray-500">No recent activity</p>
                <p className="text-sm text-gray-400">Activity will appear here when work begins</p>
              </div>
            ) : (
              <>
                {projects.map((project) => (
                  <div key={project.id} className="flex items-start gap-4 p-4 rounded-lg bg-gradient-to-r from-blue-50 to-blue-100 border border-blue-200">
                    <div className="bg-blue-500 p-2 rounded-full">
                      <Activity className="h-4 w-4 text-white" />
                    </div>
                    <div className="flex-1">
                      <p className="font-semibold text-blue-900">{project.title} updated</p>
                      <p className="text-sm text-blue-700">{project.description}</p>
                      <p className="text-xs text-blue-500 mt-1">{format(new Date(project.updatedAt || project.createdAt), "MMM dd, yyyy 'at' h:mm a")}</p>
                    </div>
                  </div>
                ))}
                {estimates.map((estimate) => (
                  <div key={estimate.id} className="flex items-start gap-4 p-4 rounded-lg bg-gradient-to-r from-green-50 to-green-100 border border-green-200">
                    <div className="bg-green-500 p-2 rounded-full">
                      <Receipt className="h-4 w-4 text-white" />
                    </div>
                    <div className="flex-1">
                      <p className="font-semibold text-green-900">Estimate {estimate.estimateNumber} created</p>
                      <p className="text-sm text-green-700">{estimate.title}</p>
                      <p className="text-xs text-green-500 mt-1">{format(new Date(estimate.createdAt), "MMM dd, yyyy 'at' h:mm a")}</p>
                    </div>
                  </div>
                ))}
                {invoices.map((invoice) => (
                  <div key={invoice.id} className="flex items-start gap-4 p-4 rounded-lg bg-gradient-to-r from-purple-50 to-purple-100 border border-purple-200">
                    <div className="bg-purple-500 p-2 rounded-full">
                      <DollarSign className="h-4 w-4 text-white" />
                    </div>
                    <div className="flex-1">
                      <p className="font-semibold text-purple-900">Invoice {invoice.invoiceNumber} generated</p>
                      <p className="text-sm text-purple-700">{formatCurrency(Number(invoice.total))}</p>
                      <p className="text-xs text-purple-500 mt-1">{format(new Date(invoice.createdAt), "MMM dd, yyyy 'at' h:mm a")}</p>
                    </div>
                  </div>
                ))}
              </>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );

  // Projects Tab Component
  const ProjectsTab = () => (
    <div className="space-y-4 sm:space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-xl sm:text-2xl font-bold text-green-500 flex items-center gap-2">
          <Building className="h-5 w-5 sm:h-6 sm:w-6 flex-shrink-0" />
          My Projects
        </h2>
      </div>
      
      {/* Mobile-Friendly Projects List */}
      <div className="grid grid-cols-1 gap-4 sm:gap-6">
        {projects.length === 0 ? (
          <Card className="bg-gradient-to-br from-white to-gray-50 border-gray-200">
            <CardContent className="p-8 sm:p-12 text-center">
              <Building className="h-12 w-12 sm:h-16 sm:w-16 text-gray-400 mx-auto mb-4" />
              <h3 className="text-base sm:text-lg font-semibold text-gray-600 mb-2">No Projects Yet</h3>
              <p className="text-sm sm:text-base text-gray-500">Your projects will appear here once work begins.</p>
            </CardContent>
          </Card>
        ) : projects.map((project) => (
          <Card key={project.id} className="remodra-card border-slate-200 dark:border-slate-700 shadow-lg hover:shadow-xl transition-all duration-300">
            <CardHeader className="bg-gradient-to-r from-slate-50 to-slate-100 dark:from-slate-800/50 dark:to-slate-800 rounded-t-lg p-4 sm:p-6 border-b border-slate-200 dark:border-slate-700">
              <div className="flex flex-col sm:flex-row justify-between items-start gap-2 sm:gap-4">
                <div className="flex-1">
                  <CardTitle className="text-base sm:text-lg text-green-500 break-words">{project.title}</CardTitle>
                  <CardDescription className="text-sm sm:text-base text-gray-600 mt-1 break-words">{project.description}</CardDescription>
                </div>
                <div className="self-start">{getStatusBadge(project.status)}</div>
              </div>
            </CardHeader>
            <CardContent className="p-4 sm:p-6">
              {/* Start & finish dates only */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-5">
                <div className="flex items-center gap-3 p-3 sm:p-4 bg-slate-50 dark:bg-slate-800/50 rounded-xl border border-slate-200 dark:border-slate-600">
                  <Clock className="h-5 w-5 text-green-600 dark:text-green-400 flex-shrink-0" />
                  <div className="min-w-0 flex-1">
                    <p className="text-xs sm:text-sm text-slate-500 dark:text-slate-400 font-medium">Started</p>
                    <p className="text-sm sm:text-base font-semibold text-slate-800 dark:text-slate-200 truncate">{project.startDate && !isNaN(new Date(project.startDate).getTime()) ? format(new Date(project.startDate), "MMM dd, yyyy") : 'Not set'}</p>
                  </div>
                </div>
                <div className="flex items-center gap-3 p-3 sm:p-4 bg-slate-50 dark:bg-slate-800/50 rounded-xl border border-slate-200 dark:border-slate-600">
                  <Calendar className="h-5 w-5 text-green-600 dark:text-green-400 flex-shrink-0" />
                  <div className="min-w-0 flex-1">
                    <p className="text-xs sm:text-sm text-slate-500 dark:text-slate-400 font-medium">Est. completion</p>
                    <p className="text-sm sm:text-base font-semibold text-slate-800 dark:text-slate-200 truncate">{project.endDate && !isNaN(new Date(project.endDate).getTime()) ? format(new Date(project.endDate), "MMM dd, yyyy") : 'Not set'}</p>
                  </div>
                </div>
              </div>
              
              {/* Progress bar — accurate (from tasks or status), modern style */}
              <div className="space-y-3">
                <div className="flex justify-between items-baseline">
                  <span className="text-sm font-medium text-slate-600 dark:text-slate-400">Progress</span>
                  <span className="text-sm font-bold tabular-nums text-green-600 dark:text-green-400">{Math.min(100, Math.max(0, project.progress ?? 0))}%</span>
                </div>
                <div className="relative w-full h-3 sm:h-3.5 bg-slate-200 dark:bg-slate-700 rounded-full overflow-hidden">
                  <div
                    className="absolute inset-y-0 left-0 rounded-full transition-all duration-500 ease-out"
                    style={{
                      width: `${Math.min(100, Math.max(0, project.progress ?? 0))}%`,
                      background: 'linear-gradient(90deg, #22c55e 0%, #16a34a 50%, #15803d 100%)',
                      boxShadow: '0 0 12px rgba(34, 197, 94, 0.4)'
                    }}
                  />
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );

  // Estimates Tab Component
  const EstimatesTab = () => (
    <div className="space-y-4 sm:space-y-6">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-2">
        <h2 className="text-xl sm:text-2xl font-bold text-green-500 flex items-center gap-2">
          <FileText className="h-5 w-5 sm:h-6 sm:w-6 flex-shrink-0" />
          My Estimates
        </h2>
      </div>
      
      {/* Mobile-Friendly Estimates Grid */}
      <div className="grid grid-cols-1 gap-4 sm:gap-6">
        {estimates.length === 0 ? (
          <div className="col-span-full">
            <Card className="remodra-card">
              <CardContent className="p-8 sm:p-12 text-center">
                <FileText className="h-12 w-12 sm:h-16 sm:w-16 text-slate-600 mx-auto mb-4" />
                <h3 className="text-base sm:text-lg font-semibold text-slate-800 mb-2">No Estimates Yet</h3>
                <p className="text-sm sm:text-base text-slate-600">Your estimates will appear here when created.</p>
              </CardContent>
            </Card>
          </div>
        ) : estimates.map((estimate) => (
          <Card key={estimate.id} className="remodra-card hover:shadow-xl transition-all duration-300">
            <CardHeader className="bg-gradient-to-r from-slate-50 to-slate-100 rounded-t-lg border-b border-slate-200 p-4 sm:p-6">
              <div className="flex flex-col sm:flex-row justify-between items-start gap-2 sm:gap-4">
                <div className="flex-1">
                  <CardTitle className="text-base sm:text-lg text-green-500">#{estimate.estimateNumber}</CardTitle>
                  <CardDescription className="text-xs sm:text-sm text-slate-700">Issued: {format(new Date(estimate.createdAt), "MMM dd, yyyy")}</CardDescription>
                </div>
                <div className="self-start">{getStatusBadge(estimate.status)}</div>
              </div>
            </CardHeader>
            <CardContent className="p-4 sm:p-6">
              <div className="space-y-3 sm:space-y-4">
                <div>
                  <p className="font-semibold text-slate-800 mb-1 text-base sm:text-lg break-words">{estimate.title}</p>
                  <p className="text-slate-600 text-sm sm:text-base mb-3 break-words">{estimate.description || 'No description provided'}</p>
                </div>
                {/* LARGE Price Display for Elderly Users */}
                <div className="bg-gradient-to-r from-green-500/20 to-green-600/20 p-4 sm:p-6 rounded-lg border-2 border-green-500/40">
                  <div className="flex items-center justify-center gap-2">
                    <DollarSign className="h-6 w-6 sm:h-7 sm:w-7 text-green-500 flex-shrink-0" />
                    <span className="text-3xl sm:text-4xl font-bold text-green-500 tabular-nums">{formatCurrency(Number(estimate.total || 0))}</span>
                  </div>
                  <p className="text-sm sm:text-base text-green-600 font-medium mt-2 text-center">Total Estimate</p>
                </div>
              </div>
            </CardContent>
            {/* LARGE Touch-Friendly Buttons */}
            <CardFooter className="flex flex-col sm:flex-row justify-end gap-3 bg-slate-50 dark:bg-slate-800/50 rounded-b-lg border-t border-slate-200 dark:border-slate-700 p-4">
              {estimate.isSigned && (
                <div className="w-full sm:w-auto flex items-center gap-2 px-4 py-2 bg-green-100 border-2 border-green-500 rounded-lg">
                  <CheckCircle className="h-5 w-5 text-green-600 flex-shrink-0" />
                  <span className="text-green-700 font-semibold text-sm sm:text-base">Signed</span>
                </div>
              )}
              {estimate.status === "converted" && (
                <div className="w-full sm:w-auto flex items-center gap-2 px-4 py-2 bg-blue-100 border-2 border-blue-500 rounded-lg">
                  <CheckCircle className="h-5 w-5 text-blue-600 flex-shrink-0" />
                  <span className="text-blue-700 font-semibold text-sm sm:text-base">Converted to Invoice</span>
                </div>
              )}
              <Button 
                variant="outline" 
                onClick={() => handleViewEstimate(estimate.id)}
                className="w-full sm:w-auto min-h-[52px] text-base font-semibold border-2 border-green-500 text-green-600 hover:bg-green-50"
              >
                <Eye className="h-5 w-5 mr-2 flex-shrink-0" />
                View Details
              </Button>
              {(estimate.status === "sent" || estimate.status === "pending") && !estimate.isSigned && (
                <Button 
                  onClick={() => handleApproveEstimate(estimate.id)}
                  className="w-full sm:w-auto min-h-[52px] text-base font-semibold bg-green-500 hover:bg-green-600 text-white"
                >
                  <CheckCircle className="h-5 w-5 mr-2 flex-shrink-0" />
                  Approve Estimate
                </Button>
              )}
            </CardFooter>
          </Card>
        ))}
      </div>
    </div>
  );

  // Invoices Tab Component
  const InvoicesTab = () => {
    // Use the imported formatCurrency from utils which ensures 2 decimal places

    const getPaymentProgress = (invoice: any) => {
      const total = Number(invoice.total) || 0;
      const paid = Number(invoice.amountPaid) || 0;
      return total > 0 ? (paid / total) * 100 : 0;
    };

    const getPaymentStatusColor = (invoice: any) => {
      const progress = getPaymentProgress(invoice);
      if (progress >= 100) return 'bg-green-500';
      if (progress > 0) return 'bg-orange-500';
      return 'bg-slate-400';
    };

    return (
      <div className="space-y-4 sm:space-y-6">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-2">
          <h2 className="text-xl sm:text-2xl font-bold text-green-500 flex items-center gap-2">
            <Receipt className="h-5 w-5 sm:h-6 sm:w-6 flex-shrink-0" />
            My Invoices
          </h2>
          <div className="text-xs sm:text-sm text-slate-600">
            {invoices.length} invoice{invoices.length !== 1 ? 's' : ''} total
          </div>
        </div>
        
        {/* Mobile-Friendly Invoices Grid */}
        <div className="grid grid-cols-1 gap-4 sm:gap-6">
          {invoices.length === 0 ? (
            <div className="col-span-full">
              <Card className="remodra-card">
                <CardContent className="p-8 sm:p-12 text-center">
                  <Receipt className="h-12 w-12 sm:h-16 sm:w-16 text-slate-600 mx-auto mb-4" />
                  <h3 className="text-base sm:text-lg font-semibold text-slate-800 mb-2">No Invoices Yet</h3>
                  <p className="text-sm sm:text-base text-slate-600">Your invoices will appear here when generated.</p>
                </CardContent>
              </Card>
            </div>
          ) : invoices.map((invoice) => {
            const total = Number(invoice.total) || 0;
            const paid = Number(invoice.amountPaid) || 0;
            const pending = total - paid;
            const progress = getPaymentProgress(invoice);
            const isOverdue = invoice.dueDate && new Date(invoice.dueDate) < new Date() && invoice.status !== 'paid';
            
            return (
              <Card key={invoice.id} className="remodra-card hover:shadow-xl transition-all duration-300">
                <CardHeader className="bg-gradient-to-r from-slate-50 to-slate-100 rounded-t-lg border-b border-slate-200 p-4 sm:p-6">
                  <div className="flex flex-col sm:flex-row justify-between items-start gap-2 sm:gap-4">
                    <div className="flex-1">
                      <CardTitle className="text-base sm:text-lg text-green-500">#{invoice.invoiceNumber}</CardTitle>
                      <CardDescription className="text-xs sm:text-sm text-slate-700 mt-1">
                        Due: {format(new Date(invoice.dueDate || invoice.createdAt), "MMM dd, yyyy")}
                        {isOverdue && <span className="text-red-500 font-bold ml-2 block sm:inline">• OVERDUE</span>}
                      </CardDescription>
                    </div>
                    <div className="self-start">{getStatusBadge(invoice.status)}</div>
                  </div>
                </CardHeader>
                <CardContent className="p-4 sm:p-6">
                  <div className="space-y-3 sm:space-y-4">
                    <p className="font-semibold text-slate-800 text-base sm:text-lg break-words">{invoice.title}</p>
                    
                    {/* Payment Progress - Mobile Optimized */}
                    <div className="space-y-2">
                      <div className="flex justify-between text-sm sm:text-base">
                        <span className="text-slate-600">Payment Progress</span>
                        <span className="font-bold text-slate-800">{progress.toFixed(0)}%</span>
                      </div>
                      <div className="w-full bg-slate-200 rounded-full h-3 sm:h-4">
                        <div 
                          className={`h-3 sm:h-4 rounded-full transition-all duration-300 ${getPaymentStatusColor(invoice)}`}
                          style={{ width: `${Math.min(progress, 100)}%` }}
                        ></div>
                      </div>
                    </div>

                    {/* LARGE Amount Display for Elderly - Mobile Stacked */}
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                      <div className="text-center p-4 bg-slate-100 rounded-lg border-2 border-slate-300">
                        <p className="text-xs sm:text-sm text-slate-600 font-medium">Total</p>
                        <p className="text-xl sm:text-2xl font-bold text-slate-800 tabular-nums">{formatCurrency(total)}</p>
                      </div>
                      <div className="text-center p-4 bg-green-50 rounded-lg border-2 border-green-500/50">
                        <p className="text-xs sm:text-sm text-green-600 font-medium">Paid</p>
                        <p className="text-xl sm:text-2xl font-bold text-green-600 tabular-nums">{formatCurrency(paid)}</p>
                      </div>
                      <div className="text-center p-4 bg-red-50 rounded-lg border-2 border-red-500/50">
                        <p className="text-xs sm:text-sm text-red-600 font-medium">Amount Due</p>
                        <p className="text-xl sm:text-2xl font-bold text-red-600 tabular-nums">{formatCurrency(pending)}</p>
                      </div>
                    </div>

                    {/* Payment History - Simplified for Mobile */}
                    {invoice.payments && invoice.payments.length > 0 && (
                      <div className="bg-blue-50 p-3 sm:p-4 rounded-lg border border-blue-200">
                        <p className="text-xs sm:text-sm text-blue-600 font-semibold mb-2">Recent Payments</p>
                        <div className="space-y-1 sm:space-y-2">
                          {invoice.payments.slice(0, 2).map((payment: any, index: number) => (
                            <div key={index} className="flex justify-between text-xs sm:text-sm">
                              <span className="text-blue-700 truncate mr-2">
                                {payment.paymentMethod} • {format(new Date(payment.paymentDate), 'MMM dd')}
                              </span>
                              <span className="font-bold text-blue-700 whitespace-nowrap">
                                {formatCurrency(Number(payment.amount))}
                              </span>
                            </div>
                          ))}
                          {invoice.payments.length > 2 && (
                            <p className="text-xs text-blue-600 mt-1">
                              +{invoice.payments.length - 2} more
                            </p>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                </CardContent>
                {/* HUGE Touch-Friendly Payment Buttons */}
                <CardFooter className="flex flex-col gap-3 bg-slate-50 dark:bg-slate-800/50 rounded-b-lg border-t border-slate-200 dark:border-slate-700 p-4">
                  {(invoice.status === "pending" || invoice.status === "partially_paid") && pending > 0 && (
                    <Button 
                      onClick={() => handlePayInvoice(invoice.id)}
                      className="w-full min-h-[60px] text-lg font-bold bg-green-500 hover:bg-green-600 text-white shadow-lg"
                    >
                      <DollarSign className="h-6 w-6 mr-2 flex-shrink-0" />
                      Pay {formatCurrency(pending)} Now
                    </Button>
                  )}
                  <div className="flex flex-col sm:flex-row gap-2 w-full">
                    <Button 
                      variant="outline" 
                      onClick={() => handleViewInvoice(invoice.id)}
                      className="flex-1 min-h-[48px] text-base font-semibold border-2 border-green-500 text-green-600 hover:bg-green-50"
                    >
                      <Eye className="h-5 w-5 mr-2 flex-shrink-0" />
                      View Details
                    </Button>
                    <Button 
                      variant="outline" 
                      onClick={() => handleDownloadInvoice(invoice)}
                      className="flex-1 min-h-[48px] text-base font-semibold border-2 border-slate-300 hover:bg-slate-50"
                    >
                      <Download className="h-5 w-5 mr-2 flex-shrink-0" />
                      Download PDF
                    </Button>
                  </div>
                </CardFooter>
              </Card>
            );
          })}
        </div>
      </div>
    );
  };

  // Schedule Tab Component
  const ScheduleTab = () => (
    <div className="space-y-4 sm:space-y-6">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-2">
        <h2 className="text-xl sm:text-2xl font-bold text-green-500 flex items-center gap-2">
          <Calendar className="h-5 w-5 sm:h-6 sm:w-6 flex-shrink-0" />
          My Schedule
        </h2>
        <div className="text-xs sm:text-sm text-slate-600">
          {appointments.length} appointment{appointments.length !== 1 ? 's' : ''} scheduled
        </div>
      </div>
      
      {/* Mobile-Friendly Appointments Grid */}
      <div className="grid grid-cols-1 gap-4 sm:gap-6">
        {appointments.length === 0 ? (
          <div className="col-span-full">
            <Card className="remodra-card">
              <CardContent className="p-8 sm:p-12 text-center">
                <Calendar className="h-12 w-12 sm:h-16 sm:w-16 text-slate-600 mx-auto mb-4" />
                <h3 className="text-base sm:text-lg font-semibold text-slate-800 mb-2">No Appointments Yet</h3>
                <p className="text-sm sm:text-base text-slate-600">Your scheduled appointments will appear here.</p>
              </CardContent>
            </Card>
          </div>
        ) : appointments.map((appointment) => (
          <Card key={appointment.id} className="remodra-card hover:shadow-xl transition-all duration-300">
            <CardHeader className="bg-gradient-to-r from-slate-50 to-slate-100 rounded-t-lg border-b border-slate-200 p-4 sm:p-6">
              <div className="flex flex-col sm:flex-row justify-between items-start gap-2 sm:gap-4">
                <div className="flex-1">
                  <CardTitle className="text-base sm:text-lg text-green-500">{appointment.title}</CardTitle>
                  <CardDescription className="text-xs sm:text-sm text-slate-700">
                    {format(new Date(appointment.startTime), "MMM dd, yyyy 'at' h:mm a")}
                  </CardDescription>
                </div>
                <div className="self-start">{getStatusBadge(appointment.status)}</div>
              </div>
            </CardHeader>
            <CardContent className="p-4 sm:p-6">
              <div className="space-y-3 sm:space-y-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div className="p-3 sm:p-4 bg-gradient-to-r from-slate-50 to-slate-100 rounded-lg border border-slate-200">
                    <p className="text-xs sm:text-sm text-slate-600 font-medium">Duration</p>
                    <p className="text-sm sm:text-base text-slate-800 font-semibold">
                      {format(new Date(appointment.startTime), "h:mm a")} - {format(new Date(appointment.endTime), "h:mm a")}
                    </p>
                  </div>
                  <div className="p-3 sm:p-4 bg-gradient-to-r from-slate-50 to-slate-100 rounded-lg border border-slate-200">
                    <p className="text-xs sm:text-sm text-slate-600 font-medium">Type</p>
                    <p className="text-sm sm:text-base text-slate-800 font-semibold">{appointment.type || 'Consultation'}</p>
                  </div>
                </div>
                {appointment.description && (
                  <div className="p-3 sm:p-4 bg-gradient-to-r from-slate-50 to-slate-100 rounded-lg border border-slate-200">
                    <p className="text-xs sm:text-sm text-slate-600 font-medium">Description</p>
                    <p className="text-sm sm:text-base text-slate-800 break-words">{appointment.description}</p>
                  </div>
                )}
              </div>
            </CardContent>
            <CardFooter className="bg-slate-50 dark:bg-slate-800/50 rounded-b-lg border-t border-slate-200 dark:border-slate-700 p-4">
              <div className="flex flex-col sm:flex-row gap-2 w-full">
                <Button 
                  variant="outline" 
                  onClick={() => handleRescheduleAppointment(appointment.id)}
                  className="flex-1 min-h-[48px] text-base font-semibold border-2 border-green-500 text-green-600 hover:bg-green-50"
                >
                  <Calendar className="h-5 w-5 mr-2 flex-shrink-0" />
                  Reschedule
                </Button>
                <AlertDialog>
                  <AlertDialogTrigger asChild>
                    <Button 
                      variant="outline" 
                      className="flex-1 min-h-[48px] text-base font-semibold border-2 border-red-300 text-red-600 hover:bg-red-50"
                    >
                      <X className="h-5 w-5 mr-2 flex-shrink-0" />
                      Cancel
                    </Button>
                  </AlertDialogTrigger>
                  <AlertDialogContent>
                    <AlertDialogHeader>
                      <AlertDialogTitle>Cancel Appointment</AlertDialogTitle>
                      <AlertDialogDescription>
                        Are you sure you want to cancel this appointment? This action cannot be undone. The contractor will be notified of the cancellation.
                      </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel>Keep Appointment</AlertDialogCancel>
                      <AlertDialogAction
                        onClick={() => handleCancelAppointment(appointment.id)}
                        className="bg-red-600 hover:bg-red-700"
                      >
                        Cancel Appointment
                      </AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              </div>
            </CardFooter>
          </Card>
        ))}
      </div>
    </div>
  );

  // Messages Tab Component
  const MessagesTab = () => (
    <div className="space-y-4 sm:space-y-6">
      <div className="remodra-card p-4 sm:p-6">
        {/* Mobile-Optimized Chat Header */}
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 sm:gap-4 mb-4 sm:mb-6">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 sm:w-12 sm:h-12 bg-gradient-to-br from-green-500 to-green-600 rounded-xl flex items-center justify-center flex-shrink-0">
              <MessageCircle className="h-5 w-5 sm:h-6 sm:w-6 text-white" />
            </div>
            <div>
              <h2 className="text-lg sm:text-2xl font-bold text-green-500">AI Assistant</h2>
              <p className="text-xs sm:text-sm text-slate-600">Get instant answers about your projects</p>
            </div>
          </div>
          <Badge className="bg-green-500/20 text-green-500 border-green-500/30 text-sm px-3 py-1">
            Online
          </Badge>
        </div>
        
        {/* Welcome Message - Mobile Friendly */}
        <div className="bg-slate-100 rounded-xl p-3 sm:p-4 mb-4">
          <div className="flex items-start gap-2 sm:gap-3">
            <div className="w-8 h-8 bg-green-500 rounded-full flex items-center justify-center flex-shrink-0">
              <MessageCircle className="h-4 w-4 text-white" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-slate-800 font-semibold text-sm sm:text-base">AI Assistant</p>
              <p className="text-slate-600 text-xs sm:text-sm break-words">Ask me about your projects, estimates, invoices, or schedule. I'm here to help!</p>
            </div>
          </div>
        </div>
        
        {/* Chat Container - Mobile Height Optimized + iOS Safe */}
        <div className="rounded-xl overflow-hidden border border-slate-200 h-[400px] sm:h-[500px]">
          <SimpleChat clientId={clientId || '4'} />
        </div>
      </div>
    </div>
  );

  // Defensive: Null checks for all fields in render
  const safeClientName = clientInfo?.name || clientInfo?.firstName || clientInfo?.email || 'Client';
  const safeClientEmail = clientInfo?.email || 'N/A';
  const safeClientPhone = clientInfo?.phone || 'N/A';
  const safeClientAddress = clientInfo?.address || 'N/A';

  return (
    <ErrorBoundary>
      <div className="remodra-layout">
        <div className="remodra-main-mobile">
          <div className="remodra-content">
            <main className="p-4 sm:p-6 md:p-8 space-y-4 sm:space-y-6 md:space-y-8 bg-white dark:bg-slate-900 min-h-screen max-w-4xl mx-auto">
              {/* Header with Remodra branding - Mobile Optimized */}
              <div className="text-center mb-6 sm:mb-8">
                <div className="flex justify-center mb-4 sm:mb-6">
                  <div className="w-12 h-12 sm:w-16 sm:h-16 bg-gradient-to-br from-green-500 to-green-700 rounded-xl flex items-center justify-center shadow-lg">
                    <User className="h-6 w-6 sm:h-8 sm:w-8 text-white" />
                  </div>
                </div>
                <h1 className="text-2xl sm:text-3xl md:text-4xl font-bold text-green-500 mb-2 sm:mb-3 px-4">
                  Client Portal
                </h1>
                <p className="text-base sm:text-lg md:text-xl text-slate-700 px-4">
                  Welcome back, <span className="text-green-500 font-semibold break-words">{safeClientName}</span>
                </p>
                <div className="mt-3 sm:mt-4 flex flex-col sm:flex-row justify-center gap-2 sm:gap-4 text-sm text-slate-600 px-4">
                  <span className="flex items-center justify-center gap-2 break-words">
                    <Mail className="h-4 w-4 flex-shrink-0" />
                    <span className="truncate max-w-[250px]">{safeClientEmail}</span>
                  </span>
                  <span className="flex items-center justify-center gap-2 whitespace-nowrap">
                    <Phone className="h-4 w-4 flex-shrink-0" />
                    {safeClientPhone}
                  </span>
                </div>
              </div>

              {/* Back Button */}

              {/* Tabs - Enhanced Visual Feedback */}
              <Tabs value={activeTab} onValueChange={handleTabChange} className="space-y-4 sm:space-y-6">
                <div className="overflow-x-auto scrollbar-hide -mx-4 sm:mx-0 flex justify-center">
                  <TabsList className="inline-flex min-w-full sm:grid sm:grid-cols-5 w-auto sm:w-full max-w-4xl bg-white dark:bg-slate-800 border-2 border-slate-300 dark:border-slate-600 shadow-sm px-4 sm:px-0 rounded-lg justify-center items-center">
                    <TabsTrigger value="overview" className="flex-shrink-0 text-slate-700 text-xs sm:text-sm px-3 sm:px-4 py-3 min-h-[48px] data-[state=active]:bg-slate-200 data-[state=active]:text-slate-800 data-[state=active]:shadow-lg data-[state=active]:scale-105 data-[state=active]:border-2 data-[state=active]:border-slate-300 hover:bg-slate-100 hover:text-slate-800 active:scale-95 transition-all duration-200 rounded-md">
                      <Home className="h-5 w-5 sm:h-5 sm:w-5 sm:mr-2 text-slate-600 data-[state=active]:text-slate-800" />
                      <span className="hidden sm:inline font-semibold">Overview</span>
                    </TabsTrigger>
                    <TabsTrigger value="projects" className="flex-shrink-0 text-slate-700 text-xs sm:text-sm px-3 sm:px-4 py-3 min-h-[48px] data-[state=active]:bg-slate-200 data-[state=active]:text-slate-800 data-[state=active]:shadow-lg data-[state=active]:scale-105 data-[state=active]:border-2 data-[state=active]:border-slate-300 hover:bg-slate-100 hover:text-slate-800 active:scale-95 transition-all duration-200 rounded-md">
                      <FolderOpen className="h-5 w-5 sm:h-5 sm:w-5 sm:mr-2 text-slate-600 data-[state=active]:text-slate-800" />
                      <span className="hidden sm:inline font-semibold">Projects</span>
                    </TabsTrigger>
                    <TabsTrigger value="estimates" className="flex-shrink-0 text-slate-700 text-xs sm:text-sm px-3 sm:px-4 py-3 min-h-[48px] data-[state=active]:bg-slate-200 data-[state=active]:text-slate-800 data-[state=active]:shadow-lg data-[state=active]:scale-105 data-[state=active]:border-2 data-[state=active]:border-slate-300 hover:bg-slate-100 hover:text-slate-800 active:scale-95 transition-all duration-200 rounded-md">
                      <Receipt className="h-5 w-5 sm:h-5 sm:w-5 sm:mr-2 text-slate-600 data-[state=active]:text-slate-800" />
                      <span className="hidden sm:inline font-semibold">Estimates</span>
                    </TabsTrigger>
                    <TabsTrigger value="invoices" className="flex-shrink-0 text-slate-700 text-xs sm:text-sm px-3 sm:px-4 py-3 min-h-[48px] data-[state=active]:bg-slate-200 data-[state=active]:text-slate-800 data-[state=active]:shadow-lg data-[state=active]:scale-105 data-[state=active]:border-2 data-[state=active]:border-slate-300 hover:bg-slate-100 hover:text-slate-800 active:scale-95 transition-all duration-200 rounded-md">
                      <DollarSign className="h-5 w-5 sm:h-5 sm:w-5 sm:mr-2 text-slate-600 data-[state=active]:text-slate-800" />
                      <span className="hidden sm:inline font-semibold">Invoices</span>
                    </TabsTrigger>
                    <TabsTrigger value="schedule" className="flex-shrink-0 text-slate-700 text-xs sm:text-sm px-3 sm:px-4 py-3 min-h-[48px] data-[state=active]:bg-slate-200 data-[state=active]:text-slate-800 data-[state=active]:shadow-lg data-[state=active]:scale-105 data-[state=active]:border-2 data-[state=active]:border-slate-300 hover:bg-slate-100 hover:text-slate-800 active:scale-95 transition-all duration-200 rounded-md">
                      <Calendar className="h-5 w-5 sm:h-5 sm:w-5 sm:mr-2 text-slate-600 data-[state=active]:text-slate-800" />
                      <span className="hidden sm:inline font-semibold">Schedule</span>
                    </TabsTrigger>
                    {/* MESSAGES TAB - HIDDEN FOR NOW - WILL IMPLEMENT REAL AI LATER
                    <TabsTrigger value="messages" className="flex-shrink-0 text-slate-700 text-xs sm:text-sm px-3 sm:px-4 py-3 min-h-[48px] data-[state=active]:bg-slate-200 data-[state=active]:text-slate-800 data-[state=active]:shadow-lg data-[state=active]:scale-105 data-[state=active]:border-2 data-[state=active]:border-slate-300 hover:bg-slate-100 hover:text-slate-800 active:scale-95 transition-all duration-200 rounded-md">
                      <MessageCircle className="h-5 w-5 sm:h-5 sm:w-5 sm:mr-2 text-slate-600 data-[state=active]:text-slate-800" />
                      <span className="hidden sm:inline font-semibold">Messages</span>
                      {unreadMessages > 0 && (
                        <Badge className="ml-1 sm:ml-2 bg-red-500 text-white text-xs px-1.5 py-0.5 rounded-full">
                          {unreadMessages}
                        </Badge>
                      )}
                    </TabsTrigger>
                    */}
                  </TabsList>
                </div>
                
                <TabsContent value="overview" className="mt-8 data-[state=active]:animate-in data-[state=active]:fade-in-0 data-[state=active]:slide-in-from-top-2 duration-300">
                  <OverviewTab />
                </TabsContent>
                
                <TabsContent value="projects" className="mt-8 data-[state=active]:animate-in data-[state=active]:fade-in-0 data-[state=active]:slide-in-from-top-2 duration-300">
                  <ProjectsTab />
                </TabsContent>
                
                <TabsContent value="estimates" className="mt-8 data-[state=active]:animate-in data-[state=active]:fade-in-0 data-[state=active]:slide-in-from-top-2 duration-300">
                  <EstimatesTab />
                </TabsContent>
                
                <TabsContent value="invoices" className="mt-8 data-[state=active]:animate-in data-[state=active]:fade-in-0 data-[state=active]:slide-in-from-top-2 duration-300">
                  <InvoicesTab />
                </TabsContent>
                
                <TabsContent value="schedule" className="mt-8 data-[state=active]:animate-in data-[state=active]:fade-in-0 data-[state=active]:slide-in-from-top-2 duration-300">
                  <ScheduleTab />
                </TabsContent>
                
                {/* MESSAGES TAB CONTENT - HIDDEN FOR NOW - WILL IMPLEMENT REAL AI LATER
                <TabsContent value="messages" className="mt-8 data-[state=active]:animate-in data-[state=active]:fade-in-0 data-[state=active]:slide-in-from-top-2 duration-300">
                  <MessagesTab />
                </TabsContent>
                */}
              </Tabs>
            </main>
          </div>
        </div>
      </div>
    </ErrorBoundary>
  );
}